import { computed, type Ref } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { useHttp } from '@/shared/lib'
import {
  normalizeObjectTypeFieldsResponse,
  type PassportBuilderFieldPropsDto,
  type PassportBuilderObjectTypeFieldDto,
} from '@/features/Inventory/PassportBuilderV2/model/objectTypeFields'

/** Маркер типа псевдо-поля в пикере условий расширенного поиска. */
export const PSEUDO_SEARCH_FIELD_TYPE = 'pseudo_props'

/** Префикс field_id псевдо-размещения в условии поиска: `props:<props_id>`. */
export const PSEUDO_SEARCH_FIELD_ID_PREFIX = 'props:'

/**
 * Псевдо-размещение поля (objects_type_field_props с pseudo_sql), доступное
 * в расширенном поиске. Сериализуется в POST /search как условие
 * `{ field_id: "props:<props_id>", compare_type: "IN_LIST", value: [ids] }`.
 */
export type PseudoSearchField = {
  id: string
  props_id: number
  /** Базовое имя без i18n-суффикса «(вычисляемое)» — его добавляет UI. */
  name: string
  /** Связанный справочник, элементами которого заполняется список значений. */
  dictionary_id: number
  type: typeof PSEUDO_SEARCH_FIELD_TYPE
}

const fieldPropsListOf = (
  field: PassportBuilderObjectTypeFieldDto,
): PassportBuilderFieldPropsDto[] => {
  if (Array.isArray(field.field_props)) return field.field_props
  if (field.field_props) return [field.field_props]
  return []
}

/**
 * Извлекает из нормализованных полей типа объекта псевдо-размещения
 * с включённым расширенным поиском (`filter_search === true`).
 */
export const extractPseudoSearchFields = (
  fields: PassportBuilderObjectTypeFieldDto[],
): PseudoSearchField[] =>
  fields.flatMap((field) =>
    fieldPropsListOf(field)
      .filter((props) => props.filter_search === true)
      .map((props): PseudoSearchField => ({
        id: `${PSEUDO_SEARCH_FIELD_ID_PREFIX}${props.id}`,
        props_id: Number(props.id),
        name: props.table_view_order?.name?.trim() || field.name,
        dictionary_id: Number(props.filter_dictionary_id) || 0,
        type: PSEUDO_SEARCH_FIELD_TYPE,
      }))
      .filter((item) => Number.isFinite(item.props_id) && item.props_id > 0),
  )

/**
 * Загружает поля типа объекта (GET /object-types/{id}/fields) и возвращает
 * псевдо-размещения, доступные для условий расширенного поиска.
 */
export const useLoadPseudoSearchFields = (objectTypeId: Ref<number>) => {
  const http = useHttp()

  return useQuery({
    queryKey: computed(() => ['pseudo_search_fields', objectTypeId.value]),
    queryFn: async () => {
      const response = await http.get(`/object-types/${objectTypeId.value}/fields`)
      return extractPseudoSearchFields(normalizeObjectTypeFieldsResponse(response).fields)
    },
    enabled: computed(() => Number.isFinite(objectTypeId.value) && objectTypeId.value > 0),
    retry: false,
  })
}
